import { apiClient } from "@/interceptors";
import { savePostRequest } from "@/utilities/offline-db";
import { useConnectionStore } from "@/store/connection.store";
import {
  buildApiHeadersFromSession,
  canRunOfflineSync,
} from "@/utilities/sync-session.utils";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Error personalizado que indica que la petición fue guardada offline.
 * Permite a los consumidores de `resilientPost` distinguir un error de red
 * (donde la venta SÍ quedó guardada en PouchDB) de un error del servidor.
 */
export class OfflineSavedError extends Error {
  public readonly endpoint: string;

  constructor(endpoint: string) {
    super(`Guardado offline: ${endpoint}`);
    this.name = "OfflineSavedError";
    this.endpoint = endpoint;
  }
}

function normalizeHeaders(input: unknown): Record<string, string> {
  const base: Record<string, string> = {};

  if (typeof input === "object" && input !== null) {
    for (const key in input) {
      const value = (input as Record<string, unknown>)[key];
      if (typeof value === "string") {
        base[key] = value;
      }
    }
  }

  return buildApiHeadersFromSession(base);
}

export const resilientPost = async <TBody = any, TResponse = any>(
  endpoint: string,
  body: TBody
): Promise<TResponse> => {
  if (endpoint.includes("sales") && !canRunOfflineSync()) {
    const err = new Error(
      "No se puede sincronizar: abre la caja en la tienda antes de enviar ventas."
    );
    (err as Error & { code?: string }).code = "SYNC_SESSION_NOT_READY";
    throw err;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...normalizeHeaders(apiClient.defaults.headers.common),
  };

  const fullUrl = `${API_URL}${endpoint}`;

  try {
    const response = await apiClient.post<TResponse>(endpoint, body);

    // Petición exitosa: limpiar bandera de offline detectado
    useConnectionStore.getState().setDetectedOffline(false);

    return response.data;
  } catch (error: any) {
    const isOffline = !navigator.onLine;
    const isNetworkError =
      axios.isAxiosError(error) &&
      (error.code === "ERR_NETWORK" ||
        error.code === "ECONNABORTED" ||
        error.code === "ETIMEDOUT" ||
        !error.response);
    const hasServerResponse = axios.isAxiosError(error) && error.response;

    if (isOffline || isNetworkError) {
      await savePostRequest(fullUrl, headers, body);
      console.warn(`Guardado offline: ${endpoint}`);

      // Notificar al store global que se detectó una desconexión real
      useConnectionStore.getState().setDetectedOffline(true);

      throw new OfflineSavedError(endpoint);
    } else if (hasServerResponse) {
      console.warn(
        `Error del servidor (${error.response?.status}). No se guarda offline.`
      );
      throw error;
    } else {
      // Sin respuesta del servidor y sin detección de offline:
      // tratar como error de red y guardar offline como red de seguridad
      await savePostRequest(fullUrl, headers, body);
      console.warn(
        `Error de red no clasificado. Guardado offline como respaldo: ${endpoint}`
      );

      useConnectionStore.getState().setDetectedOffline(true);

      throw new OfflineSavedError(endpoint);
    }
  }
};
